import { Expandable } from '@warp-ds/react';
import React, { useMemo } from 'react';
import IconQuestion16 from '@warp-ds/icons/react/question-16';
import {
  calculateBreakdown, formatToMaxDecimals, priceTable, standardPrice,
} from './motorPriceCalcUtil';
import { formatToNorwegianPrice } from '../../../utils/priceUtil';

// PROD-2739 This file can be deleted when Valere goes live 1/1/25
export default function PriceCalculatorInfoExpandable() {
  const averagePricesCalculated = useMemo(
    () => priceTable.map(({
      step, min, max,
    }) => {
      const minPrice = Math.round(calculateBreakdown(min + 1).totalPrice / (min + 1));
      const maxPrice = Math.round(calculateBreakdown(max).totalPrice / max);
      return { step, minPrice, maxPrice };
    }),
    [priceTable],
  );

  return (
    <Expandable
      box
      title={(
        <div className="flex flex-row items-center">
          <IconQuestion16 className="s-icon-primary" />
          <p className="ml-8 mb-0 font-bold">Hvordan kalkulerer vi prisen din?</p>
        </div>
    )}
      expanded={false}
      className="s-bg-info-subtle hover:s-bg-info-subtle-hover my-8"
    >
      <div className="space-y-16">
        <div>
          <p>
            Annonseprisen for din bedrift avhenger av hvor mange annonser dere publiserer i løpet av et år. Vi legger sammen prisen på alle annonsene og deler det på antall annonser. Da får vi prisen på neste annonse.
          </p>
          <p>
            Rabatten øker etter hvert som du publiserer flere annonser. Det betyr at pris per annonse kan variere, selv om du er på samme trinn i prisstigen. Pristabellen ser du her:
          </p>
        </div>
        <table className="table-auto w-full text-s">
          <thead>
            <tr className="s-bg">
              <th className="pl-8 text-left sm:hidden">Trinn</th>
              <th className="pl-8 text-left hidden sm:table-cell">Pristrinn</th>
              <th className="pl-8 text-left">Antall annonser</th>
              <th className="pl-8 text-left">Rabatt</th>
              <th className="pl-8 text-left sm:hidden">Pris</th>
              <th className="pl-8 text-left hidden sm:table-cell">Annonsepris</th>
              <th className="pl-8 text-left hidden sm:table-cell">Snittpris (fra–til)</th>
            </tr>
          </thead>
          <tbody>
            {priceTable.map(({
              step, min, max, price,
            }) => {
              const { minPrice, maxPrice } = averagePricesCalculated.find((e) => e.step === step)!;
              return (
                <tr className="divide-x even:s-bg odd:s-bg-subtle" key={step}>
                  <td className="pl-8">{step}</td>
                  <td className="pl-8">{`${formatToNorwegianPrice(step === 1 || step === priceTable.length ? min : min + 1)}${step === priceTable.length ? '+' : `–${formatToNorwegianPrice(max)}`}`}</td>
                  <td className="pl-8">{`${formatToNorwegianPrice(formatToMaxDecimals(((standardPrice - price) * 100) / standardPrice, 1))} %`}</td>
                  <td className="pl-8">{`${formatToNorwegianPrice(price)} kr`}</td>
                  <td className="pl-8 hidden sm:table-cell">{`${minPrice === maxPrice ? minPrice : `${minPrice}–${maxPrice}`} kr`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div>
          <h4>Eksempel:</h4>
          <p>
            Har du 1 286 annonser ligger du på trinn 4 – på dette trinnet koster en annonse 350 kroner, men snittprisen ligger mellom 417 og 372 kroner.
            Snittprisen varierer fordi vi legger sammen annonsene til 350 kroner med de til 400, 430 og 515 kroner også.
          </p>
          <span>
            Dersom du publiserer 1 286 annonser, deles dette opp slik:
          </span>
          <ul className="list-disc list-inside marker:mr-0">
            <li>30 annonser: 515 kroner per stk. (Trinn 1)</li>
            <li>170 annonser: 430 kroner per stk. (Trinn 2)</li>
            <li>300 annonser: 400 kroner per stk. (Trinn 3)</li>
            <li>786 annonser: 350 kroner per stk. (Trinn 4)</li>
          </ul>
          <p>Snittpris blir da 376 kroner, som ville blitt annonseprisen for den gjeldende bedriften.</p>
        </div>
      </div>
    </Expandable>
  );
}
